"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

/**
 * Lightweight toast for cart confirmations.
 *
 * Auto-dismisses after 3.2s; offers a "View bag" link.
 * Uses aria-live="polite" so screen readers announce additions without
 * stealing focus from whatever the user is doing.
 *
 * Singleton — only one instance is rendered (in app/layout.tsx). Other
 * components fire via the `nysg:toast` CustomEvent dispatched on window.
 */

export type CartToastEvent = {
  productName: string;
  size: number;
};

export function CartToast() {
  const [toast, setToast] = useState<CartToastEvent | null>(null);

  useEffect(() => {
    function handler(e: Event) {
      const detail = (e as CustomEvent<CartToastEvent>).detail;
      if (!detail) return;
      setToast(detail);
      const t = setTimeout(() => setToast(null), 3200);
      return () => clearTimeout(t);
    }
    window.addEventListener("nysg:toast", handler as EventListener);
    return () => window.removeEventListener("nysg:toast", handler as EventListener);
  }, []);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4"
    >
      {toast && (
        <div
          role="status"
          className="pointer-events-auto flex max-w-md items-center gap-4 rounded-sm border border-[var(--color-foil)]/40 bg-[var(--color-ink)] px-4 py-3 text-[var(--color-bone)] shadow-2xl animate-[toast-in_180ms_ease-out]"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-foil)] text-[var(--color-ink)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12.5l5 5 9-11"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="mono-strip text-[10px] text-[var(--color-foil)]">Added to bag</div>
            <div className="truncate font-display text-sm uppercase">
              {toast.productName}
            </div>
            <div className="font-mono text-[11px] text-[var(--color-bone-dim)]">
              Size {toast.size}
            </div>
          </div>
          <Link
            href="/cart"
            className="mono-strip whitespace-nowrap rounded-sm border border-[var(--color-bone)]/30 px-3 py-1.5 text-[10px] text-[var(--color-bone)] transition hover:border-[var(--color-blood)] hover:text-[var(--color-blood)]"
          >
            View bag
          </Link>
        </div>
      )}
      <style jsx global>{`
        @keyframes toast-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

/** Fire a cart toast from anywhere in the client tree. */
export function fireCartToast(detail: CartToastEvent): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("nysg:toast", { detail }));
}
